/**
 * The winder editor's form state, and the three pure mappings around it:
 * document → form (`winderToForm`), empty → form (`emptyForm`), and one accepted
 * AI suggestion → form patch (`winderApplyPatch`).
 *
 * Extracted from CustomWinderModal so the mappings can be unit-tested without
 * rendering the editor. `winderApplyPatch` in particular had no test and no
 * possible one while it lived inside the component as a closure over `setForm`,
 * and it is where the enrichment fields shared-types v1.97.0 added were being
 * silently dropped — see the ⚠️ note on the function.
 *
 * Everything here is pure: it takes a `WinderEntry` and/or a form state and
 * returns plain values. No component state, no Firestore.
 */

import type { StorageApplianceKind, WinderEntry } from '../types';
import { capabilitiesOf } from './winderApplianceKind';
import { WINDER_DESCRIPTION_KEY } from './winderEnrichFields';
import { turnsPerDayModeOf, type TurnsPerDayMode } from './winderTurnsPerDay';

export type TriState = '' | 'yes' | 'no';

/**
 * Form state. Numbers live as strings so a half-typed value doesn't round-trip
 * through NaN, and the tri-state booleans live as `'' | 'yes' | 'no'` so
 * "not answered" stays distinguishable from "answered no" — `hasLock: false` is
 * a fact, and a fill-empty bulk run must not treat it as a gap.
 */
export interface WinderFormState {
  /**
   * Winder or safe. Always one of the two in the form — never absent — because
   * a radio group with no selection is not a state the editor should be able to
   * show. The ABSENT-means-winder contract is honoured on the way in
   * (`winderToForm` resolves it) and on the way out (`saveWinder` writes null
   * for a winder rather than an explicit 'winder'), so the form can be simple
   * without the document acquiring data it never had.
   */
  /**
   * The three capability checkboxes — what the appliance IS and DOES.
   *
   * ⚠️ THERE IS NO `applianceKind` ON THE FORM ANY MORE. The primary kind is
   * derived from these at save time by `resolveCapabilitiesWrite`, which is
   * what lets one appliance be a winder AND a safe AND a display case without
   * the curator having to nominate a winner.
   *
   * `capSafe` and `capDisplayCase` ARE `isSafe` and `hasDisplayCase` — the same
   * facts, edited as questions rather than as flags — so there are no longer
   * separate form fields for those either.
   */
  capWinder: boolean;
  capSafe: boolean;
  capDisplayCase: boolean;
  make: string;
  model: string;
  displayName: string;
  countryOfManufacture: string;
  slotCount: string;
  turnsPerDay: string[];
  /**
   * Which TPD representation the form is expressing. Both sets of inputs keep
   * their values while hidden — toggling back and forth must not destroy what
   * was typed — so this, not the presence of data, is what the save consults.
   */
  turnsPerDayMode: TurnsPerDayMode;
  turnsPerDayMin: string;
  turnsPerDayMax: string;
  rotationDirection: string;
  rotationProgramSlugs: string[];
  powerSourceSlugs: string[];
  materialSlugs: string[];
  isQuiet: TriState;
  hasLock: TriState;

  /**
   * Plain boolean, not tri-state — see the save comment in `saveWinder`.
   *
   * It IS AI-enriched (it joined `WINDER_BOOLEAN_KEYS` in v1.97.0), so the
   * "unknown" state a tri-state would preserve is not available here: an
   * untouched checkbox and a deliberate "not a safe" both read as `false`.
   * Deliberate, and inherited — but no longer true that nothing enriches it.
   */

  /** Max programmable start delay, in hours. String — see the header note. */
  startDelayMaxHours: string;
  /**
   * The nine security specs, all as strings.
   *
   * Numbers live as strings for the same reason every other numeric field here
   * does — a half-typed value must not round-trip through NaN — and the
   * tri-state `hasAnchorPoints` uses the `'' | 'yes' | 'no'` shape its
   * neighbours do, because `boolean | null` has three states and a checkbox has
   * two.
   */
  safeCertification: string;
  safeFireRatingMinutes: string;
  /** Exposure half of the fire pair — the furnace the safe is tested in. */
  safeFireExternalTempC: string;
  /** Protective half — the interior ceiling held during that exposure. */
  safeFireMaxInternalTempC: string;
  safeWeightKg: string;
  safeLockingMechanism: string;
  safeLockingBoltCount: string;
  safeWallThicknessMm: string;
  safeHasAnchorPoints: TriState;
  safeInsuranceCashRating: string;
  /** The seven flattened `displayCaseSpecs` cells. */
  displayGlazing: string;
  displayMountingStyle: string;
  displayHasUvFiltering: TriState;
  displayHasIllumination: TriState;
  displayHasHygrometer: TriState;
  displayHasHumidityControl: TriState;
  displayHasDrawer: TriState;
  priceTierSlug: string;
  description: string;
  aliases: string[];
  sourceUrls: string[];
  isDeprecated: boolean;
  /**
   * Storage path of the curated stock image, or '' for none.
   *
   * A PATH, not a download URL — the RN app signs it at render, the same
   * contract calibre images use. Uploading writes the object immediately but
   * only sets this; the doc is not updated until the admin presses Save, so
   * the field behaves like every other one in this form.
   */
  imageStoragePath: string;
}

/** A form string as a stored number; null when blank or unparseable. */
export const numOrNull = (v: string): number | null => {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** A stored optional number as a form string; '' when absent. */
export const numStr = (v: number | null | undefined): string =>
  typeof v === 'number' && Number.isFinite(v) ? String(v) : '';

export const toTri = (v: boolean | null | undefined): TriState =>
  v === true ? 'yes' : v === false ? 'no' : '';

export const fromTri = (v: TriState): boolean | null =>
  v === 'yes' ? true : v === 'no' ? false : null;

export function emptyForm(defaultMake?: string): WinderFormState {
  return {
    // A new entry starts as a winder, which is what the overwhelming majority
    // of the catalogue is, and satisfies "at least one ticked" from the outset.
    capWinder: true,
    capSafe: false,
    capDisplayCase: false,
    make: defaultMake || '',
    model: '',
    displayName: '',
    countryOfManufacture: '',
    slotCount: '',
    turnsPerDay: [],
    turnsPerDayMode: 'list',
    turnsPerDayMin: '',
    turnsPerDayMax: '',
    rotationDirection: '',
    rotationProgramSlugs: [],
    powerSourceSlugs: [],
    materialSlugs: [],
    isQuiet: '',
    hasLock: '',
    startDelayMaxHours: '',
    safeCertification: '',
    safeFireRatingMinutes: '',
    safeFireExternalTempC: '',
    safeFireMaxInternalTempC: '',
    safeWeightKg: '',
    safeLockingMechanism: '',
    safeLockingBoltCount: '',
    safeWallThicknessMm: '',
    safeHasAnchorPoints: '',
    safeInsuranceCashRating: '',
    displayGlazing: '',
    displayMountingStyle: '',
    displayHasUvFiltering: '',
    displayHasIllumination: '',
    displayHasHygrometer: '',
    displayHasHumidityControl: '',
    displayHasDrawer: '',
    priceTierSlug: '',
    description: '',
    aliases: [],
    sourceUrls: [],
    isDeprecated: false,
    imageStoragePath: '',
  };
}

export function winderToForm(w: WinderEntry): WinderFormState {
  /*
   * ⚠️ SEEDED FROM THE CLASSIFIER, not read off the raw fields, and this is
   * what stops "at least one must be ticked" blocking a save that used to work.
   *
   * A legacy entry carries no `applianceKind`, no `isSafe` and no
   * `hasDisplayCase` at all — read literally that is three empty boxes and a
   * validation error on a document nobody has touched. `capabilitiesOf` runs
   * `resolveApplianceKind`, which correctly reads it as a winder, so it opens
   * with exactly one box ticked and saves unchanged.
   *
   * It also carries the pre-B1 safes, which are recognisable only by `isSafe`
   * with no winding fields — see `resolveApplianceKind` for why that fallback
   * is permanent rather than a migration window.
   */
  const caps = capabilitiesOf(w);
  return {
    capWinder: caps.winder,
    capSafe: caps.safe,
    capDisplayCase: caps.displayCase,
    make: w.make || '',
    model: w.model || '',
    displayName: w.displayName || '',
    countryOfManufacture: w.countryOfManufacture || '',
    slotCount: typeof w.slotCount === 'number' ? String(w.slotCount) : '',
    turnsPerDay: (w.turnsPerDay || []).map((n) => String(n)),
    // A stored range opens the editor in range mode, so what the admin sees
    // matches what the RN app renders for the same document.
    turnsPerDayMode: turnsPerDayModeOf(w),
    turnsPerDayMin:
      typeof w.turnsPerDayRange?.min === 'number' ? String(w.turnsPerDayRange.min) : '',
    turnsPerDayMax:
      typeof w.turnsPerDayRange?.max === 'number' ? String(w.turnsPerDayRange.max) : '',
    rotationDirection: w.rotationDirection || '',
    rotationProgramSlugs: w.rotationProgramSlugs || [],
    powerSourceSlugs: w.powerSourceSlugs || [],
    materialSlugs: w.materialSlugs || [],
    isQuiet: toTri(w.isQuiet),
    hasLock: toTri(w.hasLock),
    startDelayMaxHours:
      typeof w.startDelayMaxHours === 'number' ? String(w.startDelayMaxHours) : '',
    safeCertification: w.safeSpecs?.certification || '',
    safeFireRatingMinutes: numStr(w.safeSpecs?.fireRatingMinutes),
    safeFireExternalTempC: numStr(w.safeSpecs?.fireExternalTempC),
    safeFireMaxInternalTempC: numStr(w.safeSpecs?.fireMaxInternalTempC),
    safeWeightKg: numStr(w.safeSpecs?.weightKg),
    safeLockingMechanism: w.safeSpecs?.lockingMechanism || '',
    safeLockingBoltCount: numStr(w.safeSpecs?.lockingBoltCount),
    safeWallThicknessMm: numStr(w.safeSpecs?.wallThicknessMm),
    safeHasAnchorPoints: toTri(w.safeSpecs?.hasAnchorPoints),
    safeInsuranceCashRating: w.safeSpecs?.insuranceCashRating || '',
    displayGlazing: w.displayCaseSpecs?.glazing || '',
    displayMountingStyle: w.displayCaseSpecs?.mountingStyle || '',
    displayHasUvFiltering: toTri(w.displayCaseSpecs?.hasUvFiltering),
    displayHasIllumination: toTri(w.displayCaseSpecs?.hasIllumination),
    displayHasHygrometer: toTri(w.displayCaseSpecs?.hasHygrometer),
    displayHasHumidityControl: toTri(w.displayCaseSpecs?.hasHumidityControl),
    displayHasDrawer: toTri(w.displayCaseSpecs?.hasDrawer),
    priceTierSlug: w.priceTierSlug || '',
    description: w.description || '',
    aliases: w.aliases || [],
    sourceUrls: w.sourceUrls || [],
    isDeprecated: w.isDeprecated === true,
    imageStoragePath: w.imageStoragePath || '',
  };
}

/**
 * One accepted AI suggestion as a patch onto the form.
 *
 * ⚠️ EVERY key in `WINDER_ENRICH_FIELDS` needs a case here, plus the description
 * pseudo-key. This is the one place the flat form state and the enrichment
 * registry have to agree, and it used to end in a `default` that assumed
 * anything unrecognised WAS the description pseudo-key. When shared-types
 * v1.97.0 added `turnsPerDayRange`, `startDelayMaxHours`, `isSafe` and the nine
 * flattened `safeSpecs` cells, all twelve fell through that default and were
 * written into the DESCRIPTION as `String(value)` — so accepting "TPD range" set
 * the description to "[object Object]" and the range itself never reached the
 * form. The damage was masked because the description key sorts last in
 * `WINDER_ENRICH_FIELD_ORDER` and overwrote the junk on its way past; unticking
 * description in the review was all it took to see it. The twelve fields simply
 * vanished, and the next save wrote them back as null.
 *
 * So the default THROWS. A missing case is a bug that has to be loud at the
 * point of the miss, not a silent write to an unrelated field.
 *
 * `prev` is read only by the two MERGING keys (`aliases`, `sourceUrls`), which
 * add to what is already there rather than replacing it.
 */
export function winderApplyPatch(
  key: string,
  value: unknown,
  prev: WinderFormState,
): Partial<WinderFormState> {
  switch (key) {
    case 'displayName':
      return { displayName: String(value) };
    case 'countryOfManufacture':
      return { countryOfManufacture: String(value) };
    case 'slotCount':
      return { slotCount: String(value) };
    case 'turnsPerDay':
      // Enrichment proposes the discrete list, so applying one is also a
      // statement that this winder has settings rather than a range — switch the
      // form back, or the applied value would be saved away.
      return {
        turnsPerDay: (value as number[]).map((n) => String(n)),
        turnsPerDayMode: 'list',
      };
    case 'rotationDirection':
      return { rotationDirection: String(value) };
    case 'rotationProgramSlugs':
      return { rotationProgramSlugs: value as string[] };
    case 'powerSourceSlugs':
      return { powerSourceSlugs: value as string[] };
    case 'materialSlugs':
      return { materialSlugs: value as string[] };
    case 'isQuiet':
      return { isQuiet: toTri(value as boolean) };
    case 'hasLock':
      return { hasLock: toTri(value as boolean) };
    case 'hasDisplayCase':
      // Same one-directional rule as `isSafe` above, for the same reason.
      return value === true ? { capDisplayCase: true } : {};
    case 'priceTierSlug':
      return { priceTierSlug: String(value) };
    // Merge rather than replace — hand-added entries survive an AI apply.
    case 'aliases':
      return { aliases: [...new Set([...prev.aliases, ...(value as string[])])] };
    case 'sourceUrls':
      return { sourceUrls: [...new Set([...prev.sourceUrls, ...(value as string[])])] };

    // ── shared-types v1.97.0 fields ──────────────────────────────────────────
    case 'turnsPerDayRange': {
      // The mirror of the `turnsPerDay` case: applying a range is also a
      // statement that this winder is programmable rather than stepped, so the
      // mode switches with it. `coerceWinderValue` has already rejected a
      // half-formed or inverted range, so both bounds are usable numbers.
      const range = value as { min: number; max: number };
      return {
        turnsPerDayMin: String(range.min),
        turnsPerDayMax: String(range.max),
        turnsPerDayMode: 'range',
      };
    }
    case 'startDelayMaxHours':
      return { startDelayMaxHours: String(value) };
    case 'isSafe':
      // Only ever ticks. `false` from the model is "I found no evidence", not
      // "this is not a safe", and treating it as the latter would untick a box
      // a curator had set.
      return value === true ? { capSafe: true } : {};

    // ── shared-types v1.101.0 ────────────────────────────────────────────────
    /*
     * Accepting `applianceKind: 'safe'` also ticks `isSafe`, because on a safe
     * entry the two are not independent: shared-types requires a `'safe'` to
     * carry `isSafe: true` so every existing consumer of the security block —
     * spec resolution, the editor's conditional panel, the insurance PDF —
     * lights up with no change of its own.
     *
     * The reverse is NOT true and must not be added: accepting `'winder'` says
     * nothing about `isSafe`, because a winder built into a safe is both.
     * `coerceWinderValue` has already rejected anything outside the union.
     */
    /*
     * The three keys that now drive CHECKBOXES rather than fields.
     *
     * Each SETS its box and never clears another. Accepting the model's
     * `applianceKind: 'safe'` ticks "is a safe" — shared-types requires a safe
     * to carry `isSafe`, and every consumer of the security block is gated on
     * it — but does NOT untick "is a winder", because a winder built into a
     * safe is both and the model is answering about the primary kind, not
     * about what to remove.
     *
     * `'winder'` ticks the winder box for the same one-directional reason. A
     * curator who disagrees unticks it themselves; an enrichment run does not
     * get to silently drop a capability a human recorded.
     */
    case 'applianceKind': {
      const kind = value as StorageApplianceKind;
      if (kind === 'safe') return { capSafe: true };
      if (kind === 'display_case') return { capDisplayCase: true };
      return { capWinder: true };
    }

    // The nine flattened `safeSpecs` cells. No `isSafe` guard here: the form
    // holds them either way and `resolveSafeSpecsWrite` drops the whole block at
    // save time when the winder is not a safe, so that invariant lives in one
    // place rather than two that could disagree.
    case 'safeCertification':
      return { safeCertification: String(value) };
    case 'safeFireRatingMinutes':
      return { safeFireRatingMinutes: String(value) };
    case 'safeFireExternalTempC':
      return { safeFireExternalTempC: String(value) };
    case 'safeFireMaxInternalTempC':
      return { safeFireMaxInternalTempC: String(value) };
    case 'safeWeightKg':
      return { safeWeightKg: String(value) };
    case 'safeLockingMechanism':
      return { safeLockingMechanism: String(value) };
    case 'safeLockingBoltCount':
      return { safeLockingBoltCount: String(value) };
    case 'safeWallThicknessMm':
      return { safeWallThicknessMm: String(value) };
    case 'safeHasAnchorPoints':
      return { safeHasAnchorPoints: toTri(value as boolean) };
    case 'safeInsuranceCashRating':
      return { safeInsuranceCashRating: String(value) };

    // The seven flattened `displayCaseSpecs` cells. No `hasDisplayCase` guard,
    // for the same reason the safe cells have no `isSafe` one: the form holds
    // them either way and `resolveDisplayCaseSpecsWrite` drops the whole block
    // at save time, so that invariant lives in one place.
    case 'displayGlazing':
      return { displayGlazing: String(value) };
    case 'displayMountingStyle':
      return { displayMountingStyle: String(value) };
    case 'displayHasUvFiltering':
      return { displayHasUvFiltering: toTri(value as boolean) };
    case 'displayHasIllumination':
      return { displayHasIllumination: toTri(value as boolean) };
    case 'displayHasHygrometer':
      return { displayHasHygrometer: toTri(value as boolean) };
    case 'displayHasHumidityControl':
      return { displayHasHumidityControl: toTri(value as boolean) };
    case 'displayHasDrawer':
      return { displayHasDrawer: toTri(value as boolean) };

    case WINDER_DESCRIPTION_KEY:
      return { description: String(value) };
    default:
      throw new Error(
        `winderApplyPatch: no form field mapped for enrichment key "${key}". ` +
          'Add a case here whenever a field joins WINDER_ENRICH_FIELDS.',
      );
  }
}
